/**
 * ManualAppointmentDialog - V1050 + V1091
 * 수기 예약 생성 다이얼로그
 * V1091: 서비스(상품) → 서브아이템(세부 시술) 2단 Select
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Phone,
  User,
  Clock,
  FileText,
  Loader2,
  Stethoscope,
  CheckCircle2,
  UserPlus,
  Search,
} from "lucide-react";
import { handleApiError } from "@/api/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UnifiedCalendar } from "@/components/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  appointmentApi,
  type ManualAppointmentRequest,
  type SlotInfo,
} from "@/api/appointment";
import { servicesApi } from "@/api/services";
import { bookingRulesApi } from "@/api/settings";
import { medicalStaffApi } from "@/api/medical-staff";
import dayjs from "dayjs";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { toast } from "sonner";
import { customerApi } from "@/api/customer";
import type { Customer } from "@/types/customer";

const manualAppointmentSchema = z.object({
  customerPhone: z
    .string()
    .min(10, "전화번호를 입력해주세요")
    .regex(/^[0-9-]+$/, "올바른 전화번호 형식이 아닙니다"),
  customerName: z.string().optional(),
  appointmentDate: z.date({ error: "예약 날짜를 선택해주세요" }),
  appointmentTime: z.string().min(1, "예약 시간을 선택해주세요"),
  serviceId: z.string().min(1, "상품을 선택해주세요"),
  subItemId: z.string().optional(),
  medicalStaffId: z.string().optional(),
  memo: z.string().optional(),
  source: z.enum(["PHONE", "MANUAL"]).default("MANUAL"), // BL-146: WALK_IN 제거
});

type ManualAppointmentFormData = z.infer<typeof manualAppointmentSchema>;

export interface ManualAppointmentPrefill {
  date?: Date;
  time?: string;
  serviceId?: number;
  subItemId?: number;
  customerPhone?: string; // 상담 상세에서 호출 시 고객 전화번호 prefill
  customerName?: string; // 상담 상세에서 호출 시 고객명 prefill
  consultId?: number; // 기존 상담에 연결 (상담 상세에서 수기 예약 시)
}

interface ManualAppointmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  prefill?: ManualAppointmentPrefill;
}

// BL-120: 기본 시간 슬롯 (병원 설정 미로딩 시 fallback)
const generateDefaultTimeSlots = () => {
  const slots: string[] = [];
  for (let hour = 8; hour <= 21; hour++) {
    for (const min of [0, 15, 30, 45]) {
      if (hour === 21 && min > 0) break;
      slots.push(
        `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`,
      );
    }
  }
  return slots;
};

const DEFAULT_TIME_SLOTS = generateDefaultTimeSlots();

export default function ManualAppointmentDialog({
  isOpen,
  onClose,
  onSuccess,
  prefill,
}: ManualAppointmentDialogProps) {
  const queryClient = useQueryClient();
  const [matchedCustomer, setMatchedCustomer] = useState<Customer | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 고객 검색 Popover
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState<
    Customer[]
  >([]);
  const [isCustomerSearching, setIsCustomerSearching] = useState(false);
  const customerSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // P1-6: TDZ 방지 — form이 뒤에 선언되므로 ref 통해 접근
  const formRef = useRef<ReturnType<
    typeof useForm<ManualAppointmentFormData>
  > | null>(null);

  // 다이얼로그 닫힐 때 상태 정리
  useEffect(() => {
    if (!isOpen) {
      setMatchedCustomer(null);
      setIsSearching(false);
      setCustomerSearchOpen(false);
      setCustomerSearchQuery("");
      setCustomerSearchResults([]);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (customerSearchTimerRef.current)
        clearTimeout(customerSearchTimerRef.current);
    }
  }, [isOpen]);

  const form = useForm<ManualAppointmentFormData>({
    resolver: zodResolver(manualAppointmentSchema),
    defaultValues: {
      customerPhone: "",
      customerName: "",
      appointmentTime: "",
      serviceId: "",
      subItemId: "",
      medicalStaffId: "",
      memo: "",
      source: "MANUAL",
    },
  });
  formRef.current = form;

  const selectedDate = form.watch("appointmentDate");

  // BL-120: 날짜별 예약 가능 슬롯 조회
  const { data: availableSlots, isLoading: isSlotsLoading } = useQuery({
    queryKey: [
      "appointment-available-slots",
      selectedDate ? format(selectedDate, "yyyy-MM-dd") : null,
    ],
    queryFn: () =>
      appointmentApi.getAvailableSlots(format(selectedDate!, "yyyy-MM-dd")),
    enabled: isOpen && !!selectedDate,
    staleTime: 1000 * 60 * 2, // 2분 캐시
  });

  // V1091: 서비스 목록 조회 (services 테이블)
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.getMyServices(),
    enabled: isOpen,
  });

  // 예약 규칙 조회 (maxAdvanceBookingDays → maxDate 계산)
  const { data: bookingRule } = useQuery({
    queryKey: ["booking-rules"],
    queryFn: () => bookingRulesApi.get(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const maxDate = bookingRule?.maxAdvanceBookingDays
    ? dayjs().add(bookingRule.maxAdvanceBookingDays, "day").toDate()
    : undefined;

  // 의료진 목록 조회
  const { data: staffList = [] } = useQuery({
    queryKey: ["medical-staff"],
    queryFn: () => medicalStaffApi.getAll(),
    enabled: isOpen,
  });

  // 노출 가능한 서비스만 필터
  const visibleServices = services.filter((s) => s.isVisible);

  // P1-6: 전화번호 디바운스 검색으로 기존 고객 자동 매칭
  const searchCustomerByPhone = useCallback(async (phone: string) => {
    const digits = phone.replace(/-/g, "");
    if (digits.length < 10) {
      setMatchedCustomer(null);
      return;
    }
    setIsSearching(true);
    try {
      const result = await customerApi.getCustomers({ q: digits, size: 1 });
      if (result.content.length > 0) {
        const customer = result.content[0];
        setMatchedCustomer(customer);
        const currentName = formRef.current?.getValues("customerName");
        if (!currentName && customer.name) {
          formRef.current?.setValue("customerName", customer.name);
        }
      } else {
        setMatchedCustomer(null);
      }
    } catch {
      setMatchedCustomer(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handlePhoneChange = useCallback(
    (rawValue: string) => {
      const formatted = formatPhoneNumber(rawValue);
      form.setValue("customerPhone", formatted, { shouldValidate: true });
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        searchCustomerByPhone(formatted);
      }, 300);
    },
    [form, searchCustomerByPhone],
  );

  // 고객 검색 (이름 또는 전화번호)
  const handleCustomerSearch = useCallback((query: string) => {
    setCustomerSearchQuery(query);
    if (customerSearchTimerRef.current)
      clearTimeout(customerSearchTimerRef.current);
    if (query.trim().length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    setIsCustomerSearching(true);
    customerSearchTimerRef.current = setTimeout(async () => {
      try {
        const result = await customerApi.getCustomers({
          q: query.trim(),
          size: 5,
        });
        setCustomerSearchResults(result.content);
      } catch {
        setCustomerSearchResults([]);
      } finally {
        setIsCustomerSearching(false);
      }
    }, 300);
  }, []);

  const selectCustomer = useCallback(
    (customer: Customer) => {
      form.setValue("customerPhone", formatPhoneNumber(customer.phone), {
        shouldValidate: true,
      });
      form.setValue("customerName", customer.name || "");
      setMatchedCustomer(customer);
      setCustomerSearchOpen(false);
      setCustomerSearchQuery("");
      setCustomerSearchResults([]);
    },
    [form],
  );

  // prefill 데이터로 폼 자동 채우기
  useEffect(() => {
    if (isOpen && prefill) {
      if (prefill.date) {
        form.setValue("appointmentDate", prefill.date);
      }
      if (prefill.time) {
        form.setValue("appointmentTime", prefill.time);
      }
      if (prefill.serviceId) {
        form.setValue("serviceId", prefill.serviceId.toString());
      }
      if (prefill.subItemId) {
        form.setValue("subItemId", prefill.subItemId.toString());
      }
      // 상담 상세에서 호출 시 고객 정보 자동 채우기
      if (prefill.customerPhone) {
        const formatted = formatPhoneNumber(prefill.customerPhone);
        form.setValue("customerPhone", formatted, { shouldValidate: true });
        // 전화번호로 기존 고객 검색 트리거
        searchCustomerByPhone(formatted);
      }
      if (prefill.customerName) {
        form.setValue("customerName", prefill.customerName);
      }
    }
  }, [isOpen, prefill, form, searchCustomerByPhone]);

  const selectedServiceId = form.watch("serviceId");
  const selectedService = visibleServices.find(
    (s) => s.id.toString() === selectedServiceId,
  );
  const subItems = selectedService?.subItems ?? [];

  const createMutation = useMutation({
    mutationFn: (data: ManualAppointmentRequest) =>
      appointmentApi.createManual(data),
    onSuccess: () => {
      toast.success("예약이 등록되었습니다");
      queryClient.invalidateQueries({ queryKey: ["consults-list"] });
      queryClient.invalidateQueries({ queryKey: ["appointments-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["appointments-summary"] });
      // 상담 상세에서 생성한 경우 상담 데이터도 갱신
      if (prefill?.consultId) {
        queryClient.invalidateQueries({ queryKey: ["consult"] });
        queryClient.invalidateQueries({ queryKey: ["consult-appointments"] });
      }
      form.reset();
      onClose();
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(`예약 등록 실패: ${handleApiError(error)}`);
    },
  });

  const onSubmit = (data: ManualAppointmentFormData) => {
    const service = visibleServices.find(
      (s) => s.id.toString() === data.serviceId,
    );
    const subItem = service?.subItems.find(
      (si) => si.id.toString() === data.subItemId,
    );

    const selectedStaffId = data.medicalStaffId
      ? Number(data.medicalStaffId)
      : undefined;

    const request: ManualAppointmentRequest = {
      customerPhone: data.customerPhone.replace(/-/g, ""),
      customerName: data.customerName || undefined,
      appointmentDate: format(data.appointmentDate, "yyyy-MM-dd"),
      appointmentTime: data.appointmentTime,
      serviceId: service ? service.id : undefined,
      serviceName: service?.name || undefined,
      subItemId: subItem ? subItem.id : undefined,
      subItemName: subItem?.name || undefined,
      medicalStaffId: selectedStaffId,
      memo: data.memo || undefined,
      source: data.source,
      consultId: prefill?.consultId,
    };
    createMutation.mutate(request);
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleServiceChange = (value: string) => {
    form.setValue("serviceId", value, { shouldValidate: true });
    // 서비스 변경 시 서브아이템 초기화
    form.setValue("subItemId", "");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            수기 예약 등록
          </DialogTitle>
          <DialogDescription>
            전화 또는 방문 고객의 예약을 직접 입력합니다.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* 고객 검색 + 전화번호 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                고객 정보
              </Label>
              <Popover
                open={customerSearchOpen}
                onOpenChange={(open) => {
                  setCustomerSearchOpen(open);
                  if (!open) {
                    setCustomerSearchQuery("");
                    setCustomerSearchResults([]);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs">
                    <Search className="h-3.5 w-3.5 mr-1" />
                    고객 검색
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="end">
                  <div className="space-y-2">
                    <Input
                      placeholder="이름 또는 전화번호 검색..."
                      value={customerSearchQuery}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      autoFocus
                    />
                    {isCustomerSearching && (
                      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        검색 중...
                      </div>
                    )}
                    {!isCustomerSearching &&
                      customerSearchQuery.trim().length >= 2 &&
                      customerSearchResults.length === 0 && (
                        <p className="text-xs text-muted-foreground py-2">
                          해당 고객이 없습니다.
                        </p>
                      )}
                    {customerSearchResults.length > 0 && (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {customerSearchResults.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            className="w-full text-left px-2 py-1.5 rounded-md hover:bg-accent text-sm transition-colors"
                            onClick={() => selectCustomer(customer)}
                          >
                            <div className="font-medium">
                              {customer.name || "이름 없음"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatPhoneNumber(customer.phone)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Label
              htmlFor="customerPhone"
              className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground"
            >
              전화번호 <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="customerPhone"
                placeholder="010-1234-5678"
                value={form.watch("customerPhone") || ""}
                onChange={(e) => handlePhoneChange(e.target.value)}
                maxLength={14}
                className={cn(
                  form.formState.errors.customerPhone && "border-destructive",
                  matchedCustomer && "border-green-500 pr-8",
                )}
              />
              {isSearching && (
                <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
              {matchedCustomer && !isSearching && (
                <CheckCircle2 className="absolute right-2.5 top-2.5 h-4 w-4 text-success" />
              )}
            </div>
            {form.formState.errors.customerPhone && (
              <p className="text-xs text-destructive">
                {form.formState.errors.customerPhone.message}
              </p>
            )}
            {matchedCustomer ? (
              <p className="text-xs text-success flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                기존 고객: {matchedCustomer.name}
                {matchedCustomer.birthDate && ` (${matchedCustomer.birthDate})`}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                {form.watch("customerPhone")?.replace(/-/g, "").length >= 10 &&
                !isSearching ? (
                  <>
                    <UserPlus className="h-3 w-3" />
                    신규 고객으로 등록됩니다.
                  </>
                ) : (
                  "전화번호 입력 시 기존 고객을 자동 검색합니다."
                )}
              </p>
            )}
          </div>

          {/* 고객 이름 */}
          <div className="space-y-2">
            <Label htmlFor="customerName" className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              고객명
            </Label>
            <Input
              id="customerName"
              placeholder="홍길동"
              {...form.register("customerName")}
            />
            <p className="text-xs text-muted-foreground">
              신규 고객인 경우 입력한 이름으로 등록됩니다.
            </p>
          </div>

          {/* 예약 날짜 — UnifiedCalendar 인라인 */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <CalendarIcon className="h-3.5 w-3.5" />
              예약 날짜 <span className="text-destructive">*</span>
              {selectedDate && (
                <span className="text-xs font-normal text-muted-foreground ml-auto">
                  {format(selectedDate, "yyyy년 M월 d일 (EEE)", { locale: ko })}
                </span>
              )}
            </Label>
            <UnifiedCalendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  form.setValue("appointmentDate", date, {
                    shouldValidate: true,
                  });
                  // BL-120: 날짜 변경 시 시간 초기화 (새 날짜의 슬롯 조회 후 선택)
                  form.setValue("appointmentTime", "", {
                    shouldValidate: true,
                  });
                }
              }}
              minDate={new Date()}
              maxDate={maxDate}
              compact
            />
            {form.formState.errors.appointmentDate && (
              <p className="text-xs text-destructive">
                {form.formState.errors.appointmentDate.message}
              </p>
            )}
          </div>

          {/* BL-120: 예약 시간 (병원 설정 기반 슬롯) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              예약 시간 <span className="text-destructive">*</span>
            </Label>
            {isSlotsLoading ? (
              <div className="flex items-center gap-2 h-10 px-3 border rounded-md text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                슬롯 조회 중...
              </div>
            ) : (
              <Select
                value={form.watch("appointmentTime")}
                onValueChange={(value) =>
                  form.setValue("appointmentTime", value, {
                    shouldValidate: true,
                  })
                }
                disabled={!selectedDate}
              >
                <SelectTrigger
                  className={cn(
                    form.formState.errors.appointmentTime &&
                      "border-destructive",
                  )}
                >
                  <SelectValue
                    placeholder={
                      selectedDate
                        ? "시간을 선택하세요"
                        : "날짜를 먼저 선택하세요"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {/* BL-139: closed=true인 경우(휴무일/지정휴무) 처리 */}
                  {availableSlots &&
                  availableSlots.length > 0 &&
                  availableSlots[0].closed ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {availableSlots[0].reason ||
                        "해당 날짜는 예약을 받지 않습니다."}
                    </div>
                  ) : availableSlots && availableSlots.length > 0 ? (
                    availableSlots.map((slot: SlotInfo) => (
                      <SelectItem
                        key={slot.time}
                        value={slot.time}
                        disabled={!slot.available}
                        className={cn(
                          !slot.available && "text-muted-foreground",
                        )}
                      >
                        <span className="flex items-center gap-2">
                          {slot.time}
                          {!slot.available && (
                            <span className="text-xs text-destructive">마감</span>
                          )}
                          {slot.available && slot.remainingSlots <= 1 && (
                            <span className="text-xs text-warning">
                              잔여 {slot.remainingSlots}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))
                  ) : availableSlots?.length === 0 && selectedDate ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      해당 날짜는 휴무일이거나 예약 가능한 시간이 없습니다.
                    </div>
                  ) : (
                    DEFAULT_TIME_SLOTS.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {form.formState.errors.appointmentTime && (
              <p className="text-xs text-destructive">
                {form.formState.errors.appointmentTime.message}
              </p>
            )}
          </div>

          {/* V1091: 서비스/상품 선택 (services 테이블 기반) — 필수 */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              상품 선택 <span className="text-destructive">*</span>
            </Label>
            {visibleServices.length > 0 ? (
              <Select
                value={form.watch("serviceId") || ""}
                onValueChange={handleServiceChange}
              >
                <SelectTrigger
                  className={cn(
                    form.formState.errors.serviceId && "border-destructive",
                  )}
                >
                  <SelectValue placeholder="상품을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {visibleServices.map((service) => (
                    <SelectItem key={service.id} value={service.id.toString()}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground py-2">
                등록된 상품이 없습니다. 예약상품 관리에서 먼저 등록해주세요.
              </p>
            )}
            {form.formState.errors.serviceId && (
              <p className="text-xs text-destructive">
                {form.formState.errors.serviceId.message}
              </p>
            )}
          </div>

          {/* V1091: 서브아이템(세부 시술) 선택 */}
          {selectedService && subItems.length > 0 && (
            <div className="space-y-2">
              <Label>세부 시술 (선택)</Label>
              <Select
                value={form.watch("subItemId") || ""}
                onValueChange={(value) =>
                  form.setValue("subItemId", value === "__none__" ? "" : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="세부 시술을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">선택 안함</SelectItem>
                  {subItems.map((item) => (
                    <SelectItem key={item.id} value={item.id.toString()}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                세부 시술은 내원 후 변경될 수 있습니다.
              </p>
            </div>
          )}

          {/* 담당 의료진 선택 (선택사항) */}
          {staffList.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Stethoscope className="h-3.5 w-3.5" />
                담당 의료진
              </Label>
              <Select
                value={form.watch("medicalStaffId") || ""}
                onValueChange={(value) =>
                  form.setValue(
                    "medicalStaffId",
                    value === "__none__" ? "" : value,
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="의료진을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">선택 안함</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id!.toString()}>
                      {staff.name}
                      {staff.position && ` (${staff.position})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* 메모 */}
          <div className="space-y-2">
            <Label htmlFor="memo" className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              메모
            </Label>
            <Textarea
              id="memo"
              placeholder="특이사항이나 요청사항을 입력하세요"
              rows={3}
              {...form.register("memo")}
            />
          </div>

          {/* 예약 소스 — BL-146: 방문 예약 항목 제거 */}
          <div className="space-y-2">
            <Label>예약 경로</Label>
            <Select
              value={form.watch("source")}
              onValueChange={(value: "PHONE" | "MANUAL") =>
                form.setValue("source", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PHONE">전화 예약</SelectItem>
                <SelectItem value="MANUAL">기타 (수기)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              취소
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  등록 중...
                </>
              ) : (
                "예약 등록"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
